import { useState } from "react";
import { useForm } from "react-hook-form";
import { useContactInformationFormFields } from "@apac/hooks";
import {
  type TravellerDetailsFormContext,
  TravellerFormFields,
  useTravellerDetailsFormFields,
} from "@apac/travelers";
import {
  UpdateUserInfoFailureReason,
  UpdateUserInfoResponseEnum,
} from "@b2bportal/auth-api";
import { updateUserInfo } from "@b2bportal/core-auth";
import type { IContactInfo as ContactInfoFormValues } from "@b2bportal/core-types";
import { FlightsNavigationFlow } from "@b2bportal/core-utilities";
import { logger } from "@hopper-b2b/datadog";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useAuth } from "@hsbc/auth";
import { apiConfig } from "@hsbc/utilities";
import { TenantFlightSteps } from "@tenant/types";

export type OnboardingFormValues = ContactInfoFormValues &
  Partial<TravellerDetailsFormContext>;

export const useOnboardingForm = () => {
  const goToNextStep = FlightsNavigationFlow.useGoToNextStep(
    TenantFlightSteps.ONBOARDING
  );
  const { t } = useI18nContext();
  const authState = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    formFields: {
      [TravellerFormFields.FirstName]: firstNameField,
      [TravellerFormFields.LastName]: lastNameField,
    },
    travellerDetailsDefaultValues,
  } = useTravellerDetailsFormFields();

  const { formFields: contactInfoFormFields, contactInfoDefaultValues } =
    useContactInformationFormFields();

  const formFields = {
    ...contactInfoFormFields,
    [TravellerFormFields.FirstName]: firstNameField,
    [TravellerFormFields.LastName]: lastNameField,
  };

  const defaultValues = {
    ...travellerDetailsDefaultValues,
    ...contactInfoDefaultValues,
  };

  const formContext = useForm<OnboardingFormValues>({
    defaultValues,
    mode: "onBlur",
  });

  // This is to register the form fields with validations and errors
  for (const [name, details] of Object.entries(formFields)) {
    formContext.register(name as keyof OnboardingFormValues, details);
  }

  /**
   * @param phoneNumber Phone number contains country code, e.g. +6599883344
   */
  const onSaveInfo = async () => {
    const { firstName, lastName, email, phoneNumber, countryCode } =
      formContext.getValues();
    const formattedPhoneNumber = countryCode + phoneNumber;

    try {
      const res = await updateUserInfo(
        { firstName, lastName, email, phoneNumber: formattedPhoneNumber },
        apiConfig
      );
      if (
        res.UpdateUserInfoResponse ===
        UpdateUserInfoResponseEnum.UpdateUserInfoSuccess
      ) {
        authState.setUserInfo({
          firstName,
          lastName,
          email,
          phoneNumber: formattedPhoneNumber,
        });
        goToNextStep();
      } else {
        if (res.reason === UpdateUserInfoFailureReason.InvalidPhoneNumber) {
          setErrorMessage(t("onboarding.error.invalidPhoneNumber"));
        } else if (res.reason === UpdateUserInfoFailureReason.InvalidEmail) {
          setErrorMessage(t("onboarding.error.invalidEmail"));
        } else {
          setErrorMessage(t("onboarding.error.genericErrorTitle"));
        }
        logger().error(`Get failure for updating user info: ${res.reason}`);
      }
    } catch (error) {
      setErrorMessage(t("onboarding.error.genericErrorTitle"));
      logger().error("Failed update user info", { error });
    }
  };

  return {
    formContext,
    errorMessage,
    handlers: {
      onSubmit: onSaveInfo,
      clearErrorMessage: () => setErrorMessage(null),
    },
  };
};
